import React, { useCallback, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { StateType } from 'reducer';
import { PodiatryConditionalOptionsType } from 'types/types';
import {
  usePersistRFVReducer,
  WhatTreatmentsItemWithText,
} from 'reducer/reasonForVisit';
import styles from 'components/podiatry/page.module.scss';
import MetaTag from 'components/meta';
import Label from 'components/common/label';
import Button from 'components/common/button';
import clsx from 'clsx';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  options: PodiatryConditionalOptionsType[] | PodiatryConditionalOptionsType;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function WhatTreatmentsDidProvide({
  nextPage,
  nextOrSubmitButton,
  options,
  isFilter,
  multiple = true,
  actionPage,
}: Props) {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [inputText, setInputText] = useState<WhatTreatmentsItemWithText[]>([]);
  const [inputTextError, setInputTextError] = useState<
    WhatTreatmentsItemWithText[]
  >([]);

  const [questionOptions, setQuestionOptions] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [state, dispatch] = usePersistRFVReducer();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: PodiatryConditionalOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        try {
          setQuestionOptions(findOptions.options);
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionOptions(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        setSelectedOption(data.value);
        setInputText(data.options);
      }
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      setSelectedOption((old) => {
        if (old.find((value) => value === item)) {
          return old.filter((data) => data !== item);
        } return [...old, item];
      });
    } else {
      setSelectedOption([item]);
    }

    if (item === 'X-Ray' || item === 'MRI' || item === 'CAT Scan') {
      setInputText((old) => {
        if (old.find((value) => value.name === item)) {
          return old.filter((data) => data.name !== item);
        } return [...old, { name: item, text: '' }];
      });
    }
    setError(false);
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    const checkPhysicalTherapy = selectedOption.find((item) => item === 'Physical therapy');

    for (let i = 0; i < inputText.length; i += 1) {
      const item = inputText[i];
      if (item.text === '') {
        setInputTextError(inputText);
        return;
      }
    }

    await dispatch({
      type: actionPage,
      name: title,
      value: selectedOption,
      options: inputText,
    });

    if (checkPhysicalTherapy) {
      nextOrSubmitButton(nextPage);
    } else {
      nextOrSubmitButton(nextPage + 1);
    }
  };

  const onChangeText = (
    name: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputText((old) => {
      if (old.find((value) => value.name === name)) {
        const newItems = old.filter((data) => data.name !== name);
        return [...newItems, { name, text: event.target.value }];
      } return [...old, { name, text: event.target.value }];
    });
    setInputTextError((old) => {
      if (old.find((value) => value.name === name)) {
        return old.filter((data) => data.name !== name);
      } return old;
    });
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
        <p className="error-message">
          {' '}
          Please select at least one option.
        </p>
      )}
      <div>
        {questionOptions.map((option) => {
          const selectedClass = selectedOption.find(
            (item) => item === option,
          );
          const inputBoxError = inputTextError.find(
            (item) => item.name === option,
          );
          const inputBoxItem = inputText.find(
            (item) => item.name === option,
          );
          return (
            <div key={option}>
              <p
                className={clsx('option', { 'selected-option': selectedClass })}
                onClick={() => onSelectOptions(option)}
                onKeyUp={() => {}}
              >
                {option}
              </p>
              {selectedClass
                    && (option === 'X-Ray'
                      || option === 'MRI'
                      || option === 'CAT Scan') && (
                      <>
                        <Label>
                          Where was this taken?
                        </Label>
                        <TextField
                          value={inputBoxItem?.text}
                          placeholder="Type..."
                          className="w-full"
                          sx={{
                            '& .MuiInputBase-root': { borderRadius: '4px' },
                            marginTop: 0.5,
                            marginBottom: 1,
                            width: '100%',
                          }}
                          size="small"
                          error={inputBoxError?.text === ''}
                          onChange={(event) => onChangeText(option, event)}
                        />
                      </>
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          Next
        </Button>
      </div>
    </>
  );
}

export default WhatTreatmentsDidProvide;
